"use client";

import type { AmmLevel } from "deso-protocol";
import { sumBy } from "es-toolkit";
import { useEffect, useMemo, useState } from "react";
import { LuChartLine as LuLineChart } from "react-icons/lu";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { CurveType } from "recharts/types/shape/Curve";

import AmmSummaryGraphTooltip from "@/components/app/trade/amm/amm-summary-graph-tooltip";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import { useOwnTheme } from "@/contexts/theme-context";
import type { TokenOrder } from "@/hooks/use-token-orderbook";
import { focusToUsd } from "@/utils/currency";
import { abbreviateNumber } from "@/utils/helpers";
import { cn } from "@/utils/lib";

export type AmmTooltipData = {
  index: number;
  totalTokensSold: number;
  totalTokensPurchased: number;
  priceInUsd: number;
  priceInQuoteCurrency: number;
};

type ChartDataPoint = {
  tokens: number;
  price: number;
  tokensSold: number;
  priceInQuoteCurrency: number;
  totalPurchased: number;
  index?: number;
} & Record<string, number | null>;

type TooltipPayloadItem = {
  payload: ChartDataPoint;
} & Record<string, number | null>;

type GraphOptions = {
  chart?: {
    stacked?: boolean;
    zoom?: { enabled?: boolean };
  };
  stroke?: {
    curve?: CurveType;
    width?: number;
  };
  fill?: {
    type?: string;
    gradient?: {
      shade?: string;
      type?: string;
      shadeIntensity?: number;
      inverseColors?: boolean;
      opacityFrom?: number;
      opacityTo?: number;
      stops?: number[];
    };
  };
  yaxis?: {
    forceNiceScale?: boolean;
    title?: {
      text?: string;
      style?: { color?: string };
    };
  };
  xaxis?: {
    title?: {
      text?: string;
      style?: { color?: string };
    };
  };
  colors?: string[];
};

const AmmSummaryGraph = ({
  orders,
  coinUsername,
  height = 300,
  delimiters,
  graphOptions = {},
  chunkTitles,
}: {
  orders: AmmLevel[] | TokenOrder[] | null;
  coinUsername: string;
  height?: number;
  delimiters?: number[];
  graphOptions?: GraphOptions;
  chunkTitles?: string[];
  maxTickAmount?: number;
}) => {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const { darkTheme } = useOwnTheme();

  const { focusExchangeRate, loading: loadingMarketData } =
    useFocusMarketData();

  const categories = useMemo(() => {
    if (!orders?.length) return [];
    return orders.map((level, index) => {
      const size = level.AmountInQuoteCurrency / level.PriceInQuoteCurrency;
      const cumsum = orders.slice(0, index + 1).reduce((acc) => size + acc, 0);
      return cumsum;
    });
  }, [orders]);

  useEffect(() => {
    if (!orders?.length || !focusExchangeRate) {
      setChartData([]);
      return;
    }

    if (!delimiters?.length) {
      const data = orders.map((level, index) => {
        const cumulativeTokens = categories[index];
        return {
          tokens: cumulativeTokens,
          price: focusToUsd(level.PriceInQuoteCurrency, focusExchangeRate),
          tokensSold: cumulativeTokens,
          priceInQuoteCurrency: level.PriceInQuoteCurrency,
          totalPurchased: sumBy(
            (orders as AmmLevel[]).slice(0, index + 1),
            (level) => level.AmountInQuoteCurrency,
          ),
        };
      });
      setChartData(data);
    } else {
      const sortedDelimiters = [...delimiters].sort((a, b) => a - b);
      const boundaries = [-Infinity, ...sortedDelimiters, Infinity];

      const data = orders.map((level, index) => {
        const cumulativeTokens = categories[index];
        const priceUsd = focusToUsd(
          level.PriceInQuoteCurrency,
          focusExchangeRate,
        );

        let chunkIndex = 0;
        for (let i = 0; i < boundaries.length - 1; i++) {
          if (priceUsd > boundaries[i] && priceUsd <= boundaries[i + 1]) {
            chunkIndex = i;
            break;
          }
        }

        const result: ChartDataPoint = {
          tokens: cumulativeTokens,
          price: priceUsd,
          tokensSold: cumulativeTokens,
          priceInQuoteCurrency: level.PriceInQuoteCurrency,
          totalPurchased: sumBy(
            (orders as AmmLevel[]).slice(0, index + 1),
            (level) => level.AmountInQuoteCurrency,
          ),
        };

        // Add series data for each chunk
        boundaries.slice(1).forEach((_, i) => {
          const chunkName = chunkTitles?.[i] || `chunk${i + 1}`;
          result[chunkName] = i === chunkIndex ? priceUsd : null;
        });

        return result;
      });

      setChartData(data);
    }
  }, [categories, chunkTitles, delimiters, focusExchangeRate, orders]);

  const maxXValue = useMemo(() => {
    if (!chartData.length) return 0;
    return Math.max(...chartData.map((point) => point.tokens));
  }, [chartData]);

  const maxYValue = useMemo(() => {
    if (!chartData.length) return 0;
    return Math.max(...chartData.map((point) => point.price));
  }, [chartData]);

  const yAxisTicks = useMemo(() => {
    const step = 2;
    const roundedMaxY = Math.ceil(maxYValue / step) * step;
    return Array.from(
      { length: Math.ceil(roundedMaxY / step) + 1 },
      (_, i) => i * step,
    );
  }, [maxYValue]);

  const xAxisTicks = useMemo(() => {
    const step = Math.max(1, Math.ceil(maxXValue / 10)); // Dynamic step based on max value
    const roundedMaxX = Math.ceil(maxXValue / step) * step;
    return Array.from(
      { length: Math.ceil(roundedMaxX / step) + 1 },
      (_, i) => i * step,
    );
  }, [maxXValue]);

  if (!orders) {
    return null;
  }

  if (loadingMarketData || !focusExchangeRate) {
    return (
      <Skeleton
        className={cn(
          "mt-2 mb-6 w-full",
          `
            h-[${height}px]
          `,
        )}
      />
    );
  }

  if (!chartData.length) {
    return (
      <Alert>
        <LuLineChart className="inline" />
        <AlertTitle>No AMM orders found.</AlertTitle>
        <AlertDescription>
          There are no AMM orders available for this token.
        </AlertDescription>
      </Alert>
    );
  }

  const gridColor = darkTheme ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";

  const areaKeys = !delimiters?.length
    ? ["price"]
    : chunkTitles || delimiters.map((_, i) => `chunk${i + 1}`);

  const colors = graphOptions.colors || ["#22c55d", "#ef4444"];

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active: boolean;
    payload: TooltipPayloadItem[];
  }) => {
    if (!active || !payload || !payload.length) {
      return null;
    }

    const dataPoint = payload[0].payload;

    return (
      <AmmSummaryGraphTooltip
        coinUsername={coinUsername}
        focusExchangeRate={focusExchangeRate}
        data={{
          index: payload[0].payload.index ?? 0,
          totalTokensSold: dataPoint.tokensSold,
          totalTokensPurchased: dataPoint.totalPurchased,
          priceInUsd: dataPoint.price,
          priceInQuoteCurrency: dataPoint.priceInQuoteCurrency,
        }}
      />
    );
  };

  return (
    <div
      className="mt-2 mb-6 flex flex-col rounded-2xl border"
      style={{ minHeight: height + 15 }}
    >
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 10, bottom: 30 }}
        >
          <defs>
            {areaKeys.map((key, index) => (
              <linearGradient
                key={`gradient-${index}`}
                id={`color-${index}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor={colors[index % colors.length]}
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor={colors[index % colors.length]}
                  stopOpacity={0.1}
                />
              </linearGradient>
            ))}
          </defs>

          <CartesianGrid
            strokeDasharray="3 3"
            stroke={gridColor}
            vertical={false}
          />

          <XAxis
            dataKey="tokens"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: gridColor, strokeWidth: 1 }}
            padding={{ left: 0, right: 10 }}
            domain={[0, xAxisTicks[xAxisTicks.length - 1]]}
            ticks={xAxisTicks}
            type="number"
            tickFormatter={(value) => abbreviateNumber(value)}
            label={
              graphOptions.xaxis?.title?.text
                ? {
                    value: graphOptions.xaxis.title.text,
                    position: "insideBottom",
                    offset: -5,
                    style: {
                      textAnchor: "middle",
                      fill:
                        graphOptions.xaxis.title.style?.color ||
                        (darkTheme ? "#eee" : "#000"),
                      fontSize: 12,
                    },
                  }
                : undefined
            }
          />

          <YAxis
            tick={{ fontSize: 11 }}
            tickFormatter={(value) => `$${abbreviateNumber(value)}`}
            tickLine={false}
            axisLine={false}
            width={80}
            type="number"
            domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
            ticks={yAxisTicks}
            label={
              graphOptions.yaxis?.title?.text
                ? {
                    value: graphOptions.yaxis.title.text,
                    position: "insideLeft",
                    angle: -90,
                    offset: 0,
                    style: {
                      textAnchor: "middle",
                      fill:
                        graphOptions.yaxis.title.style?.color ??
                        (darkTheme ? "#eee" : "#000"),
                      fontSize: 12,
                    },
                  }
                : undefined
            }
          />

          {/* @ts-expect-error this is how rechart lib works */}
          <Tooltip content={<CustomTooltip />} />

          {chunkTitles && chunkTitles.length > 0 && (
            <Legend
              align="center"
              verticalAlign="bottom"
              height={20}
              iconType="circle"
              iconSize={8}
              wrapperStyle={{
                paddingTop: 0,
                position: "relative",
                transform: "translateX(40px)",
                paddingBottom: 0,
              }}
              formatter={(value) => (
                <span
                  style={{
                    color: darkTheme ? "#eee" : "#333",
                    fontSize: "12px",
                  }}
                >
                  {value}
                </span>
              )}
              payload={areaKeys.map((key, index) => ({
                value: chunkTitles[index] || key,
                type: "circle",
                id: key,
                color: colors[index % colors.length],
              }))}
            />
          )}

          {areaKeys.map((key, index) => (
            <Area
              key={key}
              type={
                graphOptions.stroke?.curve ||
                (index === 0 ? "monotone" : "step")
              }
              dataKey={key}
              stroke={colors[index % colors.length]}
              strokeWidth={graphOptions.stroke?.width || 2}
              fillOpacity={1}
              fill={`url(#color-${index})`}
              activeDot={{
                r: 6,
                strokeWidth: 0,
                fill: colors[index % colors.length],
              }}
              connectNulls={true}
              name={chunkTitles?.[index] || key}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AmmSummaryGraph;
